import React, { FC, useMemo, useState } from 'react';
import { user } from '../../assets/icons';
import Button, { ButtonIntentE, ButtonModeE } from '../../components/Button/Button';
import Tooltip from '../../components/Tooltip/Tooltip';
import { CampaignWizardMode, ProjectStatusE, StatusE, UserRoleE } from '../../models/enums';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectIsFormDirty } from '../../store/selectors/appSelectors';
import { selectCurrentUser } from '../../store/selectors/authSelectors';
import { selectWizardStep } from '../../store/selectors/popupSelectors';
import { setQuickSave, setWizardStep } from '../../store/slices/popupSlice';
import { isMobileOrTablet } from '../../utils/helpers/mediaQueries';
import usePopup from '../../utils/hooks/usePopup';
import UnsavedChangesWarning from '../UnsavedChangesWarning';

import styles from './campaignWizard.module.scss';

type CampaignWizardFooterPropTypes = {
    mode: CampaignWizardMode;
    projectStatus?: StatusE | ProjectStatusE,
}

const CampaignWizardFooter: FC<CampaignWizardFooterPropTypes> = (props) => {
    const { mode, projectStatus } = props;
    const user = useAppSelector(selectCurrentUser);
    const currentStep = useAppSelector(selectWizardStep);
    const isFormDirty = useAppSelector(selectIsFormDirty);
    const dispatch = useAppDispatch();
    const { removePopup, createPopup } = usePopup();
    const [showSaveButton, setShowSaveButton] = useState(true);
    const [showSaveAndSendButton, setShowSaveAndSendButton] = useState(true);

    const handleBack = () => {
        if (currentStep === 0 && !isFormDirty) {
            removePopup();
        } else if (currentStep === 0 && isFormDirty) {
            createPopup(UnsavedChangesWarning);
        } else {
            dispatch(setWizardStep(currentStep - 1));
        }
    };

    const handleQuickSave = () => {
        dispatch(setQuickSave(true));
    };

    if (user.role === UserRoleE.DeliveryService && currentStep === 3 && mode !== CampaignWizardMode.EDIT && mode !== CampaignWizardMode.SHORT_EDIT) return (
        <div className={styles.footerWrapper}>
            <Button
                mode={ButtonModeE.OUTLINED}
                onClick={handleBack}
                className={styles.backButton}
                id='wizard-back-button'
            >
                Back
            </Button>
            <Button
                id='step-3-save-and-send-button'
                form='step-3-save-and-send'
                intent={ButtonIntentE.CAMPAIGN}
                useGlobalLoading
                // onClick={() => setShowSaveButton(false)}
            >
                Create campaign
            </Button>
        </div>
    );
    if (user.role === UserRoleE.DeliveryService && currentStep === 3 && (mode === CampaignWizardMode.EDIT || mode === CampaignWizardMode.SHORT_EDIT)) return (
        <div className={styles.footerWrapper}>
            <Button
                mode={ButtonModeE.OUTLINED}
                onClick={handleBack}
                className={styles.backButton}
                id='wizard-back-button'
            >
                Back
            </Button>
            <Button
                mode={isMobileOrTablet() ? ButtonModeE.OUTLINED : ButtonModeE.LINK}
                intent={ButtonIntentE.CAMPAIGN}
                icon='save'
                form='step-3-save'
                id='step-3-save-button'
                useGlobalLoading
                // onClick={() => setShowSaveAndSendButton(false)}
            >
                Save campaign
            </Button>
        </div>
    );

    return (
        <div className={styles.footerWrapper}>
            <Button
                mode={ButtonModeE.OUTLINED}
                onClick={handleBack}
                className={styles.backButton}
                id='wizard-back-button'
            >
                Back
            </Button>
            {currentStep === 3 ? (
                <div className={styles.saveButtons}>
                    {showSaveButton && (
                        <Tooltip text='Save campaign without sending to the post production company'>
                            <Button
                                mode={isMobileOrTablet() ? ButtonModeE.OUTLINED : ButtonModeE.LINK}
                                intent={ButtonIntentE.CAMPAIGN}
                                icon='save'
                                form='step-3-save'
                                id='step-3-save-button'
                                useGlobalLoading
                                onClick={() => setShowSaveAndSendButton(false)}
                            >
                                Save campaign
                            </Button>
                        </Tooltip>
                    )}
                    {
                        (mode === CampaignWizardMode.CREATE || mode === CampaignWizardMode.COPY || projectStatus === StatusE.Creating)
                        && showSaveAndSendButton && (
                            <Tooltip text='Save and send campaign to the post production company'>
                                <Button
                                    id='step-3-save-and-send-button'
                                    form='step-3-save-and-send'
                                    useGlobalLoading
                                    onClick={() => setShowSaveButton(false)}
                                >
                                    Save and send
                                </Button>
                            </Tooltip>
                        )}
                </div>
            ) : (
                <div className={styles.saveButtons}>
                    {currentStep !== 3 && (mode === CampaignWizardMode.EDIT || mode === CampaignWizardMode.SHORT_EDIT) && (
                        <Tooltip text='Save campaign changes'>
                            <Button
                                mode={isMobileOrTablet() || mode !== CampaignWizardMode.SHORT_EDIT ? ButtonModeE.OUTLINED : ButtonModeE.FILLED}
                                icon='save'
                                form={`step-${currentStep}`}
                                useGlobalLoading
                                onClick={handleQuickSave}
                            >
                                Save changes
                            </Button>
                        </Tooltip>
                    )}
                    {mode !== CampaignWizardMode.SHORT_EDIT && (
                        <Button
                            form={`step-${currentStep}`}
                            id={`wizard-continue-button-${currentStep}`}
                        >
                            Continue
                        </Button>
                    )}
                    {isMobileOrTablet() && (
                        <Button mode={ButtonModeE.OUTLINED} onClick={handleBack}>Back</Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CampaignWizardFooter;